import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import type { Tour } from './types'

/**
 * The state of the tour in progress, outside the component tree.
 *
 * A tour changes route, and changing route unmounts everything below the router — a tour kept in
 * one page's own state would die at the first step that leads elsewhere. A context above the
 * router is what survives the navigation the tour itself causes.
 */
export interface TourState {
  tour: Tour | null
  index: number
  /** The step is waiting for the page or the target: the panel says so. */
  waiting: boolean
  /** The element lit up right now, or null when the step has none. */
  target: HTMLElement | null
  /**
   * The user went back, and the step should be left alone.
   *
   * Advance conditions look at how the page is shaped RIGHT NOW: a step that says "click here to
   * open the drawer" is already satisfied if the drawer is open — exactly the situation after going
   * back. Without this, "Back" would land on the previous step, which would immediately close
   * again — indistinguishable from a broken button. Someone who went back moves on when they choose
   * to, with "Next".
   */
  rewound: boolean
}

interface TourContextValue extends TourState {
  setTarget: (target: HTMLElement | null) => void
  start: (tour: Tour, options?: { from?: number }) => void
  next: () => void
  previous: () => void
  stop: () => void
  setWaiting: (waiting: boolean) => void
}

const EMPTY_STATE: TourState = {
  tour: null,
  index: 0,
  waiting: false,
  target: null,
  rewound: false,
}

const TourContext = createContext<TourContextValue | null>(null)

export const TourProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [state, setState] = useState<TourState>(EMPTY_STATE)

  const setTarget = useCallback((target: HTMLElement | null) => {
    setState((previous) => ({ ...previous, target }))
  }, [])

  const setWaiting = useCallback((waiting: boolean) => {
    setState((previous) => ({ ...previous, waiting }))
  }, [])

  const start = useCallback((tour: Tour, options: { from?: number } = {}) => {
    setState({ tour, index: options.from ?? 0, waiting: true, target: null, rewound: false })
  }, [])

  const stop = useCallback(() => {
    setState(EMPTY_STATE)
  }, [])

  const next = useCallback(() => {
    setState((previous) => {
      if (!previous.tour) return previous
      // Past the last step the tour is over: no index left pointing at nothing.
      if (previous.index + 1 >= previous.tour.steps.length) return EMPTY_STATE
      // The previous step's target disappears right away — leaving it lit would highlight the
      // wrong thing while the next one loads.
      return { ...previous, index: previous.index + 1, waiting: true, target: null, rewound: false }
    })
  }, [])

  const previous = useCallback(() => {
    setState((previousState) => {
      if (previousState.index === 0) return previousState
      return { ...previousState, index: previousState.index - 1, waiting: true, target: null, rewound: true }
    })
  }, [])

  const value = useMemo<TourContextValue>(
    () => ({ ...state, setTarget, setWaiting, start, next, previous, stop }),
    [state, setTarget, setWaiting, start, next, previous, stop],
  )

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

export const useTour = (): TourContextValue => {
  const context = useContext(TourContext)
  if (!context) throw new Error('useTour must be used within a TourProvider')
  return context
}
